import { ReactNode, useMemo } from 'react';
import { Table } from '@tanstack/react-table';

import { TableBody } from '@/components/ui/table';

import type { DataTableRow, TableAction } from '../types';
import { DataTableRow as TableRowComponent } from './data-table-row';

interface HierarchicalTableBodyProps<T extends DataTableRow> {
  table: Table<T>;
  selectable?: boolean;
  actions?: TableAction<T>[];
  expandedRows?: Set<string>;
  onRowExpand?: (rowId: string, expanded: boolean) => void;
  getRowChildren?: (row: T) => T[];
  getRowLevel?: (row: T) => number;
  renderExpandIcon?: (row: T, isExpanded: boolean) => ReactNode;
  rowClassName?: (row: T) => string;
}

interface HierarchicalRow extends DataTableRow {
  parentId?: string;
}

export function HierarchicalTableBody<T extends DataTableRow>({
  table,
  selectable = false,
  actions = [],
  expandedRows,
  onRowExpand,
  getRowChildren,
  getRowLevel,
  renderExpandIcon,
  rowClassName
}: HierarchicalTableBodyProps<T>) {
  const visibleRows = useMemo(() => {
    return table.getRowModel().rows.filter((row) => {
      const rowData = row.original;
      const level = getRowLevel?.(rowData) || 0;

      if (level === 0) return true;

      const hierarchicalData = rowData as HierarchicalRow;
      const parentId = hierarchicalData.parentId;
      if (!parentId) return false;

      if (!expandedRows?.has(parentId)) return false;

      if (level === 2) {
        const parent = table.getRowModel().rows.find((r) => r.id === parentId);
        if (parent) {
          const grandParentData = parent.original as HierarchicalRow;
          const grandParentId = grandParentData.parentId;
          if (grandParentId && !expandedRows?.has(grandParentId)) return false;
        }
      }

      return true;
    });
  }, [table, expandedRows, getRowLevel]);

  return (
    <TableBody>
      {visibleRows.map((row) => {
        const rowData = row.original;
        const isExpanded = expandedRows?.has(row.id) || false;
        const level = getRowLevel?.(rowData) || 0;
        const hasChildren = getRowChildren ? (getRowChildren(rowData)?.length || 0) > 0 : false;

        return (
          <TableRowComponent
            key={row.id}
            row={row}
            selectable={selectable}
            actions={actions}
            hierarchical={true}
            level={level}
            isExpanded={isExpanded}
            onToggleExpand={hasChildren && onRowExpand ? () => onRowExpand(row.id, !isExpanded) : undefined}
            renderExpandIcon={renderExpandIcon}
            rowClassName={rowClassName}
            hasChildren={hasChildren}
          />
        );
      })}
    </TableBody>
  );
}
